import { forwardRef, useImperativeHandle, useRef } from "react";
import {
  LogisticsMap,
  type LogisticsMapHandle,
  type RouteOverlay,
  type OrderOverlay,
  type Route,
  type Order,
  type VolumeUnit,
} from "../../../vendor/prism-next/map/logistics/index.js";
import { useMapReady } from "./use-map-ready";
import { usePickupMarkerLayer } from "./use-pickup-marker-layer";

export interface LogisticsMapViewProps {
  routes?: Route[];
  orders?: Order[];
  mode: "routes" | "orders";
  /** Environment volume-capacity unit (rc1.24+ requires it once per map). */
  capacityUnit: VolumeUnit;
  className?: string;
  onHover?: (index: number | null) => void;
  /** Route pin set changed (sidebar Pin, deck ✕, or data reset) — prism-next's
   *  authoritative event; the page mirrors it to the sidebar. */
  onPinnedChange?: (pinnedPathIds: readonly string[]) => void;
  /** Order pin set changed (rc1.26) — same convention, for orders (no deck). */
  onOrderPinnedChange?: (pinnedOrderIds: readonly string[]) => void;
}

export interface LogisticsMapViewHandle {
  /** The active overlay's full API (pin/focusRoute/hoverRoute/setVisible/routeColor/…
   *  — see prism-next's RouteOverlay/OrderOverlay) — null until the map is ready.
   *  The sidebars read this to drive pin/hover/visibility. */
  readonly routes: RouteOverlay | null;
  readonly orders: OrderOverlay | null;
}

/**
 * Thin wrapper over prism-next's <LogisticsMap>. Everything it does — hover +
 * detail popups, the pinned deck, and the on-map control group (Reframe · Copy PNG ·
 * Export PNG, rendered by prism-next under the zoom stack) — is prism-next's. The
 * only gap left to fill here is the warehouse pickup marker (prism-next draws a plain
 * circle and exposes no pickup-icon prop); getting the raw map instance for that layer
 * is why useMapReady still polls the handle (LogisticsMap intentionally omits onReady).
 * The exposed handle is just the overlays, for the sidebars.
 */
export const LogisticsMapView = forwardRef<LogisticsMapViewHandle, LogisticsMapViewProps>(function LogisticsMapView(
  { routes = [], orders = [], mode, capacityUnit, className, onHover, onPinnedChange, onOrderPinnedChange },
  ref,
) {
  const innerRef = useRef<LogisticsMapHandle>(null);
  const map = useMapReady(innerRef);

  usePickupMarkerLayer(map, routes);

  useImperativeHandle(
    ref,
    () => ({
      get routes() {
        return innerRef.current?.routes ?? null;
      },
      get orders() {
        return innerRef.current?.orders ?? null;
      },
    }),
    [],
  );

  return (
    // A nested-flex-grow ancestor chain doesn't reliably give a percentage-height
    // (h-full) descendant a resolvable size in every layout combination — the
    // map ended up 0×0 in testing. `absolute inset-0` sidesteps that entirely
    // (always gets a definite size from its containing block) — the CALLER is
    // responsible for making its own wrapper `relative` with a real height
    // (e.g. `min-h-0 flex-1 relative`); this component fills it exactly.
    <div className={`absolute inset-0 ${className ?? ""}`}>
      <LogisticsMap
        ref={innerRef}
        routes={routes}
        orders={orders}
        mode={mode}
        capacityUnit={capacityUnit}
        onHover={onHover}
        onPinnedChange={onPinnedChange}
        onOrderPinnedChange={onOrderPinnedChange}
        fitToData
        style={{ width: "100%", height: "100%" }}
      />
    </div>
  );
});
